<template>
  <ModalBase :show-modal="showModal" @close-modal="close">
    <template v-slot:titlebar>
      <TitleBar variant-class="title-modal">
        <template v-if="showComponent == 'details'" v-slot:pretitle>
            {{ (isEditModal) ? $gettext('EDIT'): $gettext('ADD') }}
        </template>
        <template v-slot:title>
          <div v-if="showComponent === 'details'">
            <span v-if="!$route.params['tripId']" v-translate>New trip</span>
            <span v-if="$route.params['tripId']"><translate>Trip</translate>&nbsp;{{$store.getters['tripStore/getTripNumber']}}</span>
          </div>

          <GekkoButton 
            v-if="showComponent != 'details'" 
            @click.prevent="backToInfo" 
            :text="$gettext('Go back to trip info.')"
            icon="fa-solid fa-circle-arrow-left"
            class="text-info px-0"
            size="lg"
            variant="link"
          />

          <div v-if="showComponent == 'cars'" class="d-flex justify-contents-center align-items-center">
            <span v-translate>Your cars</span>
          </div>
        </template>
        <template v-slot:buttons>
          <div v-if="showComponent == 'details'" class="modal-action-bar">
            <GekkoButton 
              @click="manageCars()"
              :text="$gettext('Manage my cars')"
              icon="fa-solid fa-pencil"
              variant="link"
              class="text-info"
            />
            <GekkoButton
              v-if="isEditModal"
              @click="createReturnTrip()"
              :text="$gettext('Create return trip')"
              icon="fa-solid fa-right-left"
              variant="link"
              class="text-info"
            />
            <GekkoButton
              v-if="isEditModal"
              @click="duplicate()"
              :text="$gettext('Duplicate')"
              icon="fa-regular fa-copy"
              variant="link"
              class="text-info"
            />
            <GekkoButton 
              v-if="isEditModal"
              :text="$gettext('Delete')" 
              icon="fa-solid fa-trash-can" 
              variant="link" 
              class="text-danger"
              v-gk-confirm="{
                title: $gettext('Delete trip?'),
                message: $gettext('Are you sure you want to delete this trip?'),
                options: {
                    variant: 'danger',
                    confirmText: $gettext('Delete')
                },
                callback: deleteTrip
              }"
            />
          </div>
        </template>
      </TitleBar>
    </template>
    <template v-slot:maincontent>
      <b-row v-show="showComponent !== 'cars'" class="h-100">
        <b-col md="5" v-show="showComponent !== 'cars'">
          <overlay-scrollbars :options="{ scrollbars: { autoHide: 'never' }, overflowBehavior: { y: 'scroll', x: 'hidden' } }">
            <AddEditTrip v-show="showComponent === 'details'" ref="addedittrip" @created="onTripCreated" @updated="onTripUpdated" @manageCars="manageCars" />
            <CopyTrip v-if="showCopyComponent" ref="copytripform" :tripDetails="tripDetails" :componentType="showComponent" @created="onTripCreated" />
          </overlay-scrollbars>
        </b-col>
        <b-col md="7">
          <GoogleMap v-show="isMapAvailable" ref="googleMap" />
          <div v-show="!isMapAvailable">
            <span v-translate class="disabled-map-message">No route available</span>
            <img src="/static/img/trips/norouteavailable.png" alt="No route available" class="disabled-map-img">
          </div>
        </b-col>
      </b-row>
      <Cars v-if="showComponent === 'cars'" />
    </template>
    <template v-slot:actions>
      <GekkoButton 
        v-if="showComponent == 'details'"
        @click="$refs.addedittrip.saveForm()"
        :text="$gettext('Save Trip')"
        icon="fa-solid fa-floppy-disk"
        type="submit"
        class="float-right position-relative"
        :loading="isWaitingForResponse"
      />
      <GekkoButton 
        v-if="showCopyComponent"
        @click="$refs.copytripform.saveForm()"
        :text="$gettext('Save Trip')"
        icon="fa-solid fa-floppy-disk"
        type="submit"
        class="float-right position-relative"
        :loading="isWaitingForResponse"
      />
    </template>
  </ModalBase>
</template>

<script>
import { mapGetters } from 'vuex';

import TitleBar from "@/components/TitleBar";
import GekkoButton from "@/components/GekkoButton";
import AddEditTrip from "@/components/trips/AddEditTrip";
import TripService from "@/store/services/trip.service";
import CopyTrip from "@/components/trips/CopyTrip";
import Cars from "@/views/settings/Cars";
import GoogleMap from "@/components/trips/GoogleMap";
import ModalBase from "@/components/base_components/ModalBase";

export default {
    name: "TripModal",
    components: { ModalBase, TitleBar, GekkoButton, AddEditTrip, CopyTrip, Cars, GoogleMap },
    props: {
        showModalOn: {
            type: Array
        }
    },
    data() {
        return {
            showModal: false,
            tripDetails: {
                number: "",
                start: {},
                end: {},
                company_profile: null,
                customer: null,
                project: null,
                car: null,
                created_by: null,
                transport_type: "car",  // default
                type: "business",  // default
                description: "",
                private_distance: null,
                total_distance: "",
                end_odometer: "",
                date: null,
                total_hours: "0.00",
                is_invoiced: false,
                invoice: null,
                route: null,  // read from the database and should never be modified
                raw_route: "",  // always calculated by user
                created: null,
                modified: null
            },
            showComponent: "details", // there are 4 components: 'details', 'return', 'copy' and 'cars'
            startPosition: null,
            endPosition: null,
            calculateRouteOnInit: false
        }
    },
    computed: {
      ...mapGetters({ 
        currentUser: 'main/getCurrentUser',
        getCompanyId: 'main/getCompanyId',
        isWaitingForResponse: 'main/isWaitingForResponse'
      }),
      isEditModal() {
        return this.$route.params['tripId'] != null;
      },
      showCopyComponent() {
        return this.showComponent == 'return' || this.showComponent == 'duplicate';
      },
      isMapAvailable() {
        if(this.$store.getters['getIsPremium'] || this.$store.getters['tripStore/getMapPolyline'].length){
          return true;
        }
        return false;
      }
    },
    watch: {
        $route: {
            immediate: true,
            handler(to) {
                this.showModal = this.showModalOn.includes(to.name);
                (this.showModal) ? document.body.classList.add('modal-on') : document.body.classList.remove('modal-on');
            }
        },
    },
    created() {
        this.tripDetails.created_by = this.currentUser.id
        this.tripDetails.company_profile = this.getCompanyId
        window.addEventListener('keydown', this.closeEventListener);
    },
    destroyed () {
        window.removeEventListener('keydown', this.closeEventListener);
    },
    methods: {
        closeEventListener(event) {
            if (this.showModal && event.key === 'Escape') this.close();
        },
        close(){
            this.$router.push({name: 'Trips'});
            this.showComponent = 'details';
        },
        onTripCreated(val) {
            this.showComponent = 'details';
            this.$emit('created', val);
        },
        onTripUpdated(val) {
            this.$emit('updated', val);
        },
        manageCars() {
            this.showComponent = 'cars';
        },
        createReturnTrip() {
            if (this.$store.getters['getIsPremium']) {
                this.showComponent = "return";
                this.swapMapMarkers();
            } else
                this.$store.commit('togglePremiumModal', 'web_trips_return');
        },
        swapMapMarkers() {
            this.$refs.googleMap.swapMarkers();
            let auxStartPosition = this.startPosition;
            this.startPosition = this.endPosition;
            this.endPosition = auxStartPosition;
        },
        duplicate() {
          if (this.$store.getters['getIsPremium'])
              this.showComponent = "duplicate";
          else
              this.$store.commit('togglePremiumModal', 'web_trips_copy');
        },
        backToInfo() {
            if (this.showComponent == "return") {
                this.swapMapMarkers();
            }
            else if (this.showComponent == "cars")
                this.$refs.addedittrip.getCars();
            this.showComponent = "details";
        },
        deleteTrip() {
            let vm = this;
            let tripId = this.$route.params['tripId'];

            TripService.destroy(tripId).then(function() {
                vm.$emit('deleted', tripId);
                vm.$store.commit('setAlert', {
                    title: vm.$gettext("Trip deleted"),
                    text: vm.$gettext("The trip was deleted correctly."),
                    variant: "success",
                    duration: 4
                });
                vm.$router.push({name: "Trips"});
            }).catch(function (error) {
                let msg = this.$gettext('The trip could not be deleted.');
                if (error.data) {
                    let firstDictKey = Object.keys(error.data)[0];
                    msg = error.data[firstDictKey];
                }
                vm.$store.commit('setAlert', {
                    title: vm.$gettext("Error deleting contact"),
                    text: msg,
                    variant: "danger",
                    duration: 4
                });
            });
        }
    }
}
</script>

<style lang="scss" scoped>
.back-arrow {
  cursor: pointer;
  margin-right: 20px;
  float: left;
  i {
    font-size: 38px;
    color: $colorBlue;
  }
}

.disabled-map-message{
    font-size: 25px;
    color: #ff5408;
    position: absolute;
    margin-left: 200px;
    margin-top: 200px;
}

.disabled-map-img {
    height: auto;
    width: 100%;
}
</style>